import React, { useState, useEffect } from 'react';
import { 
  Snackbar, 
  Alert, 
  AlertColor,
  Slide,
  SlideProps
} from '@mui/material';
import { TransitionProps } from '@mui/material/transitions';

interface Notification {
  id: string;
  message: string;
  type: AlertColor;
  duration?: number;
}

const Transition = React.forwardRef(function Transition(
  props: SlideProps,
  ref: React.Ref<unknown>,
) {
  return <Slide direction="left" ref={ref} {...props} />;
});

let notificationQueue: Notification[] = [];
let isProcessing = false;

// Global notification function
(window as any).showNotification = (message: string, type: AlertColor = 'info', duration = 4000) => {
  const notification: Notification = {
    id: `notification-${Date.now()}-${Math.random()}`,
    message,
    type,
    duration
  };

  notificationQueue.push(notification);

  // Trigger update
  if (document.body) {
    const event = new CustomEvent('notification-add', { detail: notification });
    document.body.dispatchEvent(event);
  }
};

export default function NotificationSystem() {
  const [notification, setNotification] = useState<Notification | null>(null);
  const [open, setOpen] = useState(false);
  const [queue, setQueue] = useState<Notification[]>([]);

  useEffect(() => {
    const handleNotification = (event: CustomEvent<Notification>) => {
      const newNotification = event.detail;

      // If there's already a notification showing, queue this one
      if (open && notification) {
        setQueue(prev => [...prev, newNotification]);
      } else {
        // Show immediately
        setNotification(newNotification);
        setOpen(true);
      }
    };

    document.body.addEventListener('notification-add', handleNotification as EventListener);

    return () => {
      document.body.removeEventListener('notification-add', handleNotification as EventListener);
    };
  }, [open, notification]);

  // Auto-close after duration
  useEffect(() => {
    if (notification && open) {
      const timer = setTimeout(() => {
        handleClose();
      }, notification.duration || 4000);

      return () => clearTimeout(timer);
    }
  }, [notification, open]);

  // Process queue when current notification closes
  useEffect(() => {
    if (!open && queue.length > 0) {
      // Wait a bit before showing next notification
      const timer = setTimeout(() => {
        const nextNotification = queue[0];
        setQueue(prev => prev.slice(1));
        setNotification(nextNotification);
        setOpen(true);
      }, 300);

      return () => clearTimeout(timer);
    }
  }, [open, queue]);

  const handleClose = () => {
    setOpen(false);
    setTimeout(() => {
      setNotification(null);
    }, 300);
  };

  if (!notification) return null;

  return (
    <Snackbar
      open={open}
      autoHideDuration={notification.duration || 4000}
      onClose={handleClose}
      TransitionComponent={Transition}
      anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      sx={{ 
        top: { xs: '64px', sm: '80px' } // Account for mobile nav
      }}
    >
      <Alert 
        onClose={handleClose} 
        severity={notification.type}
        variant="filled"
        sx={{ minWidth: { xs: '90%', sm: '300px' } }}
        role="alert"
        aria-live="polite"
      >
        {notification.message}
      </Alert>
    </Snackbar>
  );
}

